import _ from "lodash";
import React, { memo, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { ExpandArrowsFourOutlined, PlayFilled } from "@toddle-edu/ds-icons";

import { getThumbUrl } from "Utils";
import handleEnterKey from "Utils/handleEnterKey";
import { ATTACHMENT_TYPES } from "Utils/MessageHelpers";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";
import SkeletonLoader from "UIComponents/SkeletonLoader";

import classes from "./MediaWrapper.scss";

const mapIconsToAttachmentType = {
  [ATTACHMENT_TYPES.IMAGE]: ExpandArrowsFourOutlined,
  [ATTACHMENT_TYPES.VIDEO]: PlayFilled,
};

export const MEDIA_VISIBILITY = {
  HIDDEN: "HIDDEN",
  ONLY_HOVER: "ONLY_HOVER",
  PERSISTENT: "PERSISTENT",
};

const isHiddenOverlayItems = ({
  visibility,
  isPlaceholderVisible,
  isPreviewMessagesModal,
}) => {
  if (isPlaceholderVisible || visibility === MEDIA_VISIBILITY.HIDDEN) {
    return true;
  }

  // If isPreviewMessageModal is true, then only hide the items if not persistent
  if (isPreviewMessagesModal && visibility !== MEDIA_VISIBILITY.PERSISTENT) {
    return true;
  }
  return false;
};

const MediaWrapper = props => {
  const {
    width,
    height,
    attachment,
    onMediaClick,
    imgClassName,
    thumbnailResolution,
    overlayVisibility,
    iconVisibility,
    customIcon,
    showPlaceholder,
  } = props;

  const { name, type } = attachment;
  const { isPreviewMessagesModal } = useMessageOptions();

  const isMediaClickable =
    !isPreviewMessagesModal && _.isFunction(onMediaClick);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  const handleMediaLoad = () => setIsMediaLoaded(true);

  const containerStyle = { width: `${width}px`, height: `${height}px` };

  const { thumbUrl: src } = getThumbUrl({
    attachment: attachment,
    width: thumbnailResolution.width || width,
    height: thumbnailResolution.height || height,
    fitIn: true,
  });

  const handleClick = () => {
    if (isMediaClickable) onMediaClick(attachment);
  };

  const Icon = mapIconsToAttachmentType[type];

  // by default it will be visible on hover
  const overlayClasses = classNames(classes.overlay, {
    [classes.persistentOverlay]:
      overlayVisibility === MEDIA_VISIBILITY.PERSISTENT,
  });

  // by default it will be visible on hover
  const iconContainerClasses = classNames(classes.iconContainer, {
    [classes.persistentIconContainer]:
      iconVisibility === MEDIA_VISIBILITY.PERSISTENT,
  });

  // TODO: Enhancement to lazy load images and render actual placeholder
  // using something like blurhash

  const isPlaceholderVisible = !isMediaLoaded && showPlaceholder;
  return (
    <div
      style={containerStyle}
      tabIndex={isMediaClickable ? 0 : null}
      onKeyDown={isMediaClickable ? handleEnterKey(handleClick) : null}
      role={isMediaClickable ? "button" : "presentation"}
      onClick={isMediaClickable ? handleClick : null}
      className={classes.container}
    >
      {isHiddenOverlayItems({
        visibility: overlayVisibility,
        isPlaceholderVisible,
        isPreviewMessagesModal,
      }) ? null : (
        <div className={overlayClasses} />
      )}

      {isHiddenOverlayItems({
        visibility: iconVisibility,
        isPlaceholderVisible,
        isPreviewMessagesModal,
      }) ? null : (
        <div className={iconContainerClasses}>
          {customIcon ?? <Icon size="xx-small" variant="on" />}
        </div>
      )}

      {isPlaceholderVisible ? (
        <div className={classes.placeholderContainer}>
          <SkeletonLoader width="100%" height="100%" />
        </div>
      ) : null}
      <img
        src={src}
        onLoad={handleMediaLoad}
        className={classNames(classes.img, imgClassName)}
        loading="lazy"
        alt={
          type === ATTACHMENT_TYPES.VIDEO ? `preview for ${name} video` : name
        }
      />
    </div>
  );
};

MediaWrapper.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  attachment: PropTypes.object.isRequired,
  onMediaClick: PropTypes.func,
  imgClassName: PropTypes.string,
  overlayVisibility: PropTypes.oneOf(_.values(MEDIA_VISIBILITY)),
  iconVisibility: PropTypes.oneOf(_.values(MEDIA_VISIBILITY)),
  thumbnailResolution: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  customIcon: PropTypes.node,
  showPlaceholder: PropTypes.bool,
};

MediaWrapper.defaultProps = {
  onMediaClick: null,
  imgClassName: "",
  overlayVisibility: MEDIA_VISIBILITY.ONLY_HOVER,
  iconVisibility: MEDIA_VISIBILITY.ONLY_HOVER,
  thumbnailResolution: {},
  customIcon: null,
  showPlaceholder: true,
};

export default memo(MediaWrapper);
